import { FetchResult } from 'apollo-link';
import { ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { Action } from '../../actions';
import { changeLocation } from '../../actions/common';
import { resetGridData } from '../../actions/grid-screen';
import {
    changeGroupEditPageStatus,
    saveGroupNameEditorChanges,
    SaveGroupNameEditorChangesAction,
    showGroupEditError,
    showGroupsClientValidationErrors,
    showGroupsPartialSaveResult
} from '../../actions/groupsEdit';
import { SAVE_INTERNAL_GROUPS } from '../../actions/internalGroups/saveInternalGroups';
import {
    getInternalGroupsValueFromEditors,
    mapGroupToInternalInput,
    mapNewGroupToInternalInput
} from '../../mappers/group/group';
import {
    mapInternalGroupsAddResponseFromApiFormat,
    mapInternalGroupsUpdateResponseFromApiFormat
} from '../../mappers/internalGroup';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { BannersIdsForBannersEditHelpers } from '../../service/BannersIdsForBannersEditHelpers';
import { ErrorOrWarning } from '../../service/ErrorHelpers/ErrorHelpers';
import { ApiMethodExecutingError, NotSyncedError } from '../../service/Errors';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import {
    createInternalGroupAddHandleErrorMapper,
    createInternalGroupUpdateHandleErrorMapper
} from '../../service/GroupEditErrors/GroupsBackToFrontErrorMappers';
import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import { mapInternalGroupValidationErrors } from '../../service/InternalGroupHelpers/InternalGroupExtractErrorsHelpers/InternalGroupExtractErrorsHelpers';
import { InternalGroupValidator } from '../../service/InternalGroupHelpers/InternalGroupValidator/InternalGroupValidator';
import { LangUtils } from '../../service/Lang/Lang';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import { UrlHelpers } from '../../service/UrlHelpers';
import { BannersScreenUrlSearchQueryParams } from '../../typings/banners';
import { ErrorsEdit, PageStatus, Region } from '../../typings/common';
import { EditPageItemNameEditorsState } from '../../typings/editPageItemNameEditor';
import { DnaEpic } from '../../typings/epic';
import { addInternalAdGroups, updateInternalAdGroups } from '../../typings/graphql-grid';
import { NoticeType } from '../../typings/grid';
import { GroupId } from '../../typings/groups';
import { EditGroupData } from '../../typings/groupsEditState';
import { InternalGroupsSaveResponse } from '../../typings/internalGroups';
import { State } from '../../typings/state';
import { getShowEditorsErrorsFromServerActions } from '../groupsEdit/groupsEdit';

const showGroupCommonErrorsInNotice = (errors: ErrorsEdit): void => {
    const groups = errors.groups;

    if (!groups) {
        return;
    }

    Object.values(groups).forEach(group => {
        if (group.common && group.common.errors) {
            group.common.errors.forEach(error => {
                showNoticeMessage(NoticeType.error, error.text);
            });
        }
    });
};

const saveGroupNameEditorChangesActions = (
    groupsIds: string[],
    editors: EditPageItemNameEditorsState,
    isNew: boolean
): SaveGroupNameEditorChangesAction[] => {
    const groupsIdsWithOpenedEditors = groupsIds.filter(id => {
        const editorId = GroupsHelpers.getGroupEditPageItemEditorId(id);
        const editorData = selectEditPageItemNameEditorValue(editors, editorId, isNew);
        return editorData.isVisible;
    });

    return groupsIdsWithOpenedEditors.map(id => saveGroupNameEditorChanges(id));
};

const getSaveResponseResultActions = (
    state: State,
    response: InternalGroupsSaveResponse,
    groupIds: GroupId[],
    withRedirect: boolean = false
): Observable<Action> => {
    const {
        commonData: { login },
        editGroups: { isNew, campaignsIds, groupsIds },
        editPageItemNameEditor: editors
    } = state;
    const { errors, validations = [], updatedGroupIds } = response;

    const groupErrors = isNew
        ? createInternalGroupAddHandleErrorMapper(groupIds).mapServerErrorsToClientError(validations)
        : createInternalGroupUpdateHandleErrorMapper(groupIds).mapServerErrorsToClientError(validations);
    const editorsResultActions = getShowEditorsErrorsFromServerActions(groupErrors);
    showGroupCommonErrorsInNotice(groupErrors);

    if (campaignsIds.length === 0) {
        throw new Error('не задан номер кампании');
    }

    if (errors && errors.length > 0) {
        return of(showGroupEditError(errors[0]));
    }

    if (validations.length > 0) {
        return of(
            ...saveGroupNameEditorChangesActions(groupsIds, editors, isNew),
            showGroupsPartialSaveResult({
                updatedGroupIds,
                errors: groupErrors
            }),
            ...editorsResultActions,
            showGroupsClientValidationErrors(mapInternalGroupValidationErrors(validations, groupIds)),
            changeGroupEditPageStatus(PageStatus.LOADED)
        );
    }

    if (isNew) {
        BannersIdsForBannersEditHelpers.setCampAndGroupIdsForCreation(login, campaignsIds, [
            String(updatedGroupIds[0])
        ]);

        return of(
            changeLocation(
                UrlHelpers.getInternalBannersEditUrl({
                    [BannersScreenUrlSearchQueryParams.ulogin]: login,
                    [BannersScreenUrlSearchQueryParams.campaignsIds]: campaignsIds,
                    [BannersScreenUrlSearchQueryParams.groupsIds]: updatedGroupIds,
                    [BannersScreenUrlSearchQueryParams.isNew]: '1'
                }),
                undefined,
                false
            ),
            resetGridData()
        );
    }

    if (withRedirect) {
        return of(
            changeLocation(UrlHelpers.getGroupsListUrl(), {
                ulogin: login
            })
        );
    }

    return EMPTY;
};

export const saveInternalGroupsEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_INTERNAL_GROUPS),
        mergeMap(
            (): Observable<Action> => {
                const state = store$.value;
                const {
                    login,
                    stringDicts,
                    regions,
                    client: { translocalGeotreeType }
                } = state.commonData;
                const {
                    editGroups: { groups, isNew }
                } = state;

                const regionTree: Region[] = regions
                    ? RegionsHelpers.getRegionsTree(regions, LangUtils.getCurrentLang(), translocalGeotreeType)
                    : [];

                const groupsData = getInternalGroupsValueFromEditors(
                    groups,
                    state.regionsEditor,
                    state.editPageItemNameEditor,
                    state.datesIntervalEditor,
                    state.tagCloudTargetingEditor,
                    regionTree,
                    stringDicts
                );

                const internalGroupsIds = Object.keys(groupsData);

                const errors = internalGroupsIds.reduce((acc, key, index): ErrorOrWarning[] => {
                    const currentErrors = InternalGroupValidator.validateGroup(groupsData[key], state, index);

                    return [...acc, ...currentErrors];
                }, []);

                if (errors.length > 0) {
                    return of(
                        showGroupsClientValidationErrors(mapInternalGroupValidationErrors(errors, internalGroupsIds)),
                        changeGroupEditPageStatus(PageStatus.LOADED)
                    );
                }

                const allGroups: Array<EditGroupData> = internalGroupsIds.map(
                    (groupId: GroupId) => groupsData[groupId]
                );
                const request$: Observable<InternalGroupsSaveResponse> = isNew
                    ? from(
                          deps.groupsMutationExecutor.addInternalGroups(
                              {
                                  addItems: allGroups.map(mapNewGroupToInternalInput)
                              },
                              login
                          )
                      )
                    : from(
                          deps.groupsMutationExecutor.updateInternalGroups(
                              {
                                  updateItems: allGroups.map(mapGroupToInternalInput)
                              },
                              login
                          )
                      );

                return request$.pipe(
                    mergeMap(
                        (response: InternalGroupsSaveResponse): Observable<Action> =>
                            getSaveResponseResultActions(state, response, internalGroupsIds, true)
                    ),
                    catchError(
                        (
                            error:
                                | ApiMethodExecutingError
                                | NotSyncedError<FetchResult<updateInternalAdGroups> | FetchResult<addInternalAdGroups>>
                        ): Observable<Action> => {
                            if (error instanceof NotSyncedError) {
                                return getSaveResponseResultActions(
                                    state,
                                    isNew
                                        ? mapInternalGroupsAddResponseFromApiFormat(error.data as FetchResult<
                                              addInternalAdGroups
                                          >)
                                        : mapInternalGroupsUpdateResponseFromApiFormat(error.data as FetchResult<
                                              updateInternalAdGroups
                                          >),
                                    internalGroupsIds
                                );
                            }

                            return of(showGroupEditError(error));
                        }
                    )
                );
            }
        )
    );
};
